/**
 * Section Layout Component
 *
 * Purpose: Groups fields under an optional title with a configurable column grid.
 *          Content is always visible (no collapsing behavior).
 * Architecture: Pure presentational component using CSS Grid via MUI Box.
 *               Does NOT use FieldGroupProvider since content is always visible.
 * Dependencies: React, MUI (Box, Typography, Divider), ../types (SectionProps)
 * Author: Development Team
 * Created: 2026-02-15
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import type { SectionProps } from '../types';
import { fieldGridSx } from '../../form-layout/fieldGrid';

/**
 * Section — groups fields with optional title and responsive column grid
 *
 * Purpose: Provides visual grouping of form fields with heading and grid layout
 * Logic: Renders optional divider, optional title, then children in a CSS Grid
 *        that collapses to single column on small screens
 * Input: SectionProps — title?, columns? (1|2), divider?, children
 * Output: Box container with grid-based child layout
 */
const Section: React.FC<SectionProps> = ({ title, columns = 1, divider, children }) => (
  <Box sx={{ mb: 3 }}>
    {divider && <Divider sx={{ mb: 2 }} />}
    {title && (
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>
    )}
    <Box sx={fieldGridSx(columns)}>{children}</Box>
  </Box>
);

export default Section;
